import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiUsers } from 'react-icons/fi';
import { AuditoriumViewChat } from './auditorium-view-chat';
import { AuditoriumViewLesson } from './auditorium-view-lesson';
import { useAuditorium } from '@/hooks/useAuditorium';
import ActionCableConsumer from '@/utils/actionCableConsumer';
import { Loader } from '@/components/custom/loader';
import usePermission from '@/hooks/usePermission';
import { Error404 } from '@/components/custom/error';
import { useClient } from 'jsonapi-react';
import * as ActionCable from '@rails/actioncable';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { IconVerified } from '@/components/custom/icon';
import { DashboardContent } from '@/layouts/dashboard';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { Badge } from '@/components/ui/badge';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { TiaBanner } from '@/components/custom/tia-banner';
import tiaMonitoria from '@/assets/images/tia/TIA-MONITORIA.png';

const TEN_SECONDS_IN_MILLISECONDS = 10 * 1000;

export function AuditoriumView(props) {
  const { uri } = props;
  const playfulStudent = usePlayfulStudent();

  if (!usePermission('Visualizar monitorias', 'Monitorias')) {
    return <Error404 />;
  }

  const client = useClient();
  const { t } = useTranslation();
  const id = useParams().auditoriumId;
  const { getMessages, messages, setMessages, auditorium, getAuditorium, lastMessage, getFiles, files, setFiles, loading } = useAuditorium();
  const [chats, setChats] = useState([]);
  const [page, setPage] = useState(1);
  const [messageChannel, setMessageChannel] = useState(null);
  const [messageChatChannel, setMessageChatChannel] = useState(null);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [countUsers, setCountUsers] = useState(0);
  const [pageLesson, setPageLesson] = useState(1);
  const apiBaseUrl = client.config.url;
  const cableUrl = apiBaseUrl.replace(/^http(s)?:\/\/(.*?)\/api\/v1\/?$/, 'wss://$2/cable');
  const cable = ActionCable.createConsumer(cableUrl);
  let channel;

  const getChats = async () => {
    try {
      const { data, error } = await client.fetch(`auditoriums/${id}/auditorium_chat_messages?page[number]=${page}&page[size]=10&order=created_at desc`);

      if (error) {
        toast.error(t('toast.errorSearchChatMessage'));
        return;
      }

      if (data.length === 0) {
        setHasMoreComments(false);
        return;
      }

      if (data.length < 10) {
        setHasMoreComments(false);
      }

      if (chats) {
        setChats(prevState => [...data, ...prevState]);
      } else {
        setChats(data);
      }
    } catch {
      setHasMoreComments(false);
    }
  };

  useEffect(() => {
    if (messageChatChannel) {
      const parsedMessage = JSON.parse(messageChatChannel);
      if (parsedMessage.data.type === 'auditorium-chat-message') {
        const newMessage = parsedMessage.data.attributes;
        if (chats.length < 10) {
          setChats(prevState => [...prevState, newMessage]);
        } else {
          const lastTenComments = chats.slice(1, 10);
          setChats([...lastTenComments, newMessage]);
        }
      }
    }
  }, [messageChatChannel]);

  useEffect(() => {
    if (page !== 1) {
      getChats();
    }
  }, [page]);

  useEffect(() => {
    if (messageChannel) {
      if (messageChannel?.count) {
        setCountUsers(messageChannel?.count);
      } else {
        const parsedMessage = JSON.parse(messageChannel);
        if (parsedMessage?.data?.attributes?.file) {
          const file = files.find(item => item.id === parsedMessage?.data?.attributes?.id);
          if (!file) {
            setFiles(prevFiles => [...prevFiles, parsedMessage?.data?.attributes]);
          }
        } else {
          const message = messages.find(item => item.id === parsedMessage?.data?.attributes?.id);
          if (!message) {
            setMessages(prevMessages => [...prevMessages, parsedMessage?.data?.attributes]);
          }
        }
      }
    }
  }, [messageChannel, id]);

  useEffect(() => {
    channel = cable.subscriptions.create({
      channel: 'AuditoriumStatusChannel'
    });

    const intervalId = setInterval(() => {
      channel.send({
        action: 'relay_message',
        message: {
          auditorium_id: id,
          status: 'Online'
        }
      });
    }, TEN_SECONDS_IN_MILLISECONDS);

    async function loadData() {
      await Promise.all([getAuditorium(id), getMessages(id, pageLesson), getFiles(id), getChats()]).finally(() => setIsLoading(false));
    }

    loadData();

    return () => {
      setMessages([]);
      channel.unsubscribe();
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (pageLesson !== 1) {
      getMessages(id, pageLesson);
    }
  }, [pageLesson]);

  const breadCrumbs = {
    title: 'Comunidade',
    nav: [
      {
        route: '/auditorio',
        name: 'Monitoria',
        isActive: false
      },
      {
        route: uri,
        name: auditorium?.name,
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      breadcrumbs={breadCrumbs}
      breadcrumbsVerified={auditorium?.['official-content'] && <IconVerified tooltipPlace="right" />}
    >
      {playfulStudent && (
        <TiaBanner
          className="mb-6 md:mt-20"
          image={tiaMonitoria}
          text="Tire suas dúvidas em tempo real com professores e colegas na Monitoria! 💬"
        />
      )}

      <ActionCableConsumer
        channel="AuditoriumMessageChannel"
        setObjectChannel={setMessageChannel}
        channelKey={'auditorium_id'}
        channelId={id}
      />
      <ActionCableConsumer
        channel="AuditoriumChatMessageChannel"
        setObjectChannel={setMessageChatChannel}
        channelKey={'auditorium_id'}
        channelId={id}
      />

      <Page variant="fill">
        {!auditorium.name || isLoading ? (
          <Loader />
        ) : (
          <>
            <PageContent>
              {countUsers > 0 && (
                <Badge
                  variant={playfulStudent ? 'amber-soft' : 'more'}
                  size="xs"
                  className="cursor-default gap-1 self-start"
                >
                  <FiUsers /> {countUsers} {t('textsCommon.watchingNow')}
                </Badge>
              )}

              {messages && (
                <AuditoriumViewLesson
                  id={id}
                  lastMessage={lastMessage}
                  messages={messages}
                  auditorium={auditorium}
                  chats={chats}
                  setPage={setPageLesson}
                />
              )}
            </PageContent>

            <PageSidebar
              variant="chat"
              overflow={false}
            >
              <AuditoriumViewChat
                id={id}
                files={files}
                messageChats={chats}
                setChats={setChats}
                hasMoreComments={hasMoreComments}
                setPage={setPage}
              />
            </PageSidebar>
          </>
        )}
      </Page>
    </DashboardContent>
  );
}
